import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { cn } from "design-system/utils"
import { ReactNode } from "react"

type Props = {
  id: string
  bulletClassName: string
  text: ReactNode
  textClassName?: string
  badge?: ReactNode
  onClick?: () => void
  highlight?: boolean
  isFirst: boolean
  isLast: boolean
}

type EntityTimelineRailProps = {
  bulletClassName: string
  isFirst: boolean
  isLast: boolean
}

export function EntityIconTooltip({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: ReactNode
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn("inline-flex", className)}>{children}</span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

function EntityTimelineRail({ bulletClassName, isFirst, isLast }: EntityTimelineRailProps) {
  return (
    <div className="flex flex-col items-center">
      <div className={cn("h-2.5 w-px shrink-0", !isFirst && "bg-border")} />
      <div
        className={cn(
          "relative z-20 aspect-square size-2 shrink-0 rounded-full ring-2 ring-background",
          bulletClassName,
        )}
      />
      <div className={cn("w-px flex-1", !isLast && "bg-border")} />
    </div>
  )
}

export function EntityRow({
  id,
  bulletClassName,
  text,
  textClassName,
  badge,
  onClick,
  highlight,
  isFirst,
  isLast,
}: Props) {
  const className = cn(
    "relative w-full text-left transition-colors duration-150",
    "before:pointer-events-none before:absolute before:inset-y-0 before:-left-3 before:-right-3",
    "before:rounded-md before:bg-primary/10 before:opacity-0 before:content-['']",
    "before:transition-opacity before:duration-150",
    "[&.active-support-entity]:before:opacity-100",
    onClick && "cursor-pointer hover:bg-muted/40",
    highlight && "rounded-md ring-1 ring-warning",
  )

  const content = (
    <div className="grid grid-cols-[0.75rem_minmax(0,1fr)] items-stretch gap-x-2 pr-1">
      <EntityTimelineRail bulletClassName={bulletClassName} isFirst={isFirst} isLast={isLast} />
      <div className="flex min-w-0 items-start gap-3 py-1.5">
        {badge && <div className="flex h-4 shrink-0 items-center">{badge}</div>}
        <div
          className={cn(
            "min-w-0 flex-1 whitespace-normal break-words text-xs font-normal leading-snug text-foreground",
            textClassName,
          )}
        >
          {text}
        </div>
      </div>
    </div>
  )

  if (onClick) {
    return (
      <button id={id} type="button" data-support-entity-row className={className} onClick={onClick}>
        {content}
      </button>
    )
  }

  return (
    <div id={id} data-support-entity-row className={className}>
      {content}
    </div>
  )
}
